import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  billingApi,
  clientsApi,
  type BillingRun,
  type InvoiceRunDetail,
  type ReadyToBillGates,
  type Trip,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatDate, formatDateTime, formatPeso, humanize } from '@/lib/format';
import { adminNavItems } from '@/pages/navItems';

const GATE_LABELS: Record<string, string> = {
  pod_signed: 'POD signed',
  scans_resolved: 'Scans resolved',
  exceptions_decided: 'Exceptions decided',
};

function currentMonthPeriod() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function errorMessage(err: unknown, fallback: string) {
  return (err as { message?: string })?.message ?? fallback;
}

function runTotal(run: BillingRun) {
  return run.total ?? run.totalAmount ?? null;
}

/** Best per-trip billable amount available on a trip row. */
function tripAmount(trip: Trip) {
  return trip.finalAmount ?? trip.totalCharges ?? trip.quotedAmount ?? null;
}

function Money({ value }: { value: string | number | null | undefined }) {
  return <span className="money-cell">{formatPeso(value)}</span>;
}

export function AdminBilling() {
  const confirm = useConfirm();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canPushXero = hasPermission('billing.push_xero');

  const { data: runs, loading, error: runsLoadError, reload } = useAsync(() => billingApi.listRuns(), []);
  const [showAdd, setShowAdd] = useState(false);
  const [period, setPeriod] = useState(currentMonthPeriod());
  const [clientId, setClientId] = useState('');
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  const { data: reviewQueue, loading: queueLoading, reload: reloadQueue } = useAsync(
    () => billingApi.reviewQueue(),
    [],
  );
  const { data: clients } = useAsync(() => clientsApi.list(), []);
  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients ?? []) map.set(c.id, c.tradeName || c.legalName);
    return map;
  }, [clients]);
  const clientName = (id?: string | null) => (id ? clientNameById.get(id) : undefined) ?? 'this client';

  const [gatesByTrip, setGatesByTrip] = useState<Record<string, ReadyToBillGates>>({});
  const [gatesLoading, setGatesLoading] = useState(false);
  const [markingReady, setMarkingReady] = useState<string | null>(null);
  const [markError, setMarkError] = useState('');

  useEffect(() => {
    if (!reviewQueue?.length) return;
    let cancelled = false;
    setGatesLoading(true);
    Promise.all(reviewQueue.map((t) => billingApi.getGates(t.id).then((g) => [t.id, g] as const)))
      .then((entries) => {
        if (cancelled) return;
        setGatesByTrip(Object.fromEntries(entries));
      })
      .catch(() => {
        /* gate chips fall back to "—"; the button explains why it is disabled */
      })
      .finally(() => {
        if (!cancelled) setGatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewQueue]);

  const tripRef = (t: Trip) => t.tripNumber || t.reference || 'Trip';

  const handleMarkReady = async (trip: Trip) => {
    setMarkingReady(trip.id);
    setMarkError('');
    try {
      await billingApi.markReady(trip.id);
      toast(`${tripRef(trip)} marked ready to bill`);
      await reloadQueue();
    } catch (err) {
      setMarkError(errorMessage(err, 'Failed to mark ready to bill'));
    } finally {
      setMarkingReady(null);
    }
  };

  const [discardingId, setDiscardingId] = useState<string | null>(null);
  const [finalizingId, setFinalizingId] = useState<string | null>(null);
  const [pushingId, setPushingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const handleDownloadInvoice = async (run: BillingRun) => {
    setDownloadingInvoiceId(run.id);
    try {
      await billingApi.downloadInvoicePdf(run.id, run.invoiceNumber);
    } catch (err) {
      toast(errorMessage(err, 'The invoice PDF could not be downloaded.'), 'error');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };
  const [detailRunId, setDetailRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InvoiceRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [markingCollectedId, setMarkingCollectedId] = useState<string | null>(null);

  const [showCreditNote, setShowCreditNote] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditError, setCreditError] = useState('');

  const loadDetail = async (id: string) => {
    setDetailRunId(id);
    setDetailLoading(true);
    setDetailError('');
    try {
      const data = await billingApi.getRun(id);
      setDetail(data);
    } catch (err) {
      setDetailError(errorMessage(err, 'Failed to load invoice run detail'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailRunId(null);
    setDetail(null);
    setDetailError('');
    setShowCreditNote(false);
  };

  const runName = (run: BillingRun) =>
    run.invoiceNumber ? `${run.invoiceNumber} (${clientName(run.clientId)})` : `${clientName(run.clientId)}, ${run.period}`;

  const handleMarkCollected = async (run: BillingRun) => {
    const ok = await confirm({
      title: 'Mark invoice collected?',
      message: `Record that ${runName(run)} has been paid in full (${formatPeso(runTotal(run))} incl. VAT). Commission for pay-on-collection plans becomes payable in the next settlement.`,
      confirmLabel: 'Mark collected',
    });
    if (!ok) return;
    setMarkingCollectedId(run.id);
    try {
      await billingApi.markCollected(run.id);
      toast(`${runName(run)} marked collected`);
      await Promise.all([reload(), loadDetail(run.id)]);
    } catch (err) {
      setDetailError(errorMessage(err, 'Failed to mark invoice collected'));
    } finally {
      setMarkingCollectedId(null);
    }
  };

  const handleCreateCreditNote = async (e: FormEvent) => {
    e.preventDefault();
    if (!detailRunId) return;
    if (!creditAmount || Number(creditAmount) <= 0) {
      setCreditError('Enter a positive amount');
      return;
    }
    if (!creditReason.trim()) {
      setCreditError('Reason is required');
      return;
    }
    setCreditSaving(true);
    setCreditError('');
    try {
      const note = await billingApi.createCreditNote(detailRunId, { amount: creditAmount, reason: creditReason });
      setShowCreditNote(false);
      setCreditAmount('');
      setCreditReason('');
      toast(`Credit note ${note.creditNoteNumber ? `${note.creditNoteNumber} ` : ''}issued: ${formatPeso(note.total ?? creditAmount)} incl. VAT`);
      await loadDetail(detailRunId);
    } catch (err) {
      setCreditError(errorMessage(err, 'Failed to create credit note'));
    } finally {
      setCreditSaving(false);
    }
  };

  const canDiscardRun = (row: BillingRun) =>
    row.canDiscard ??
    (['draft', 'previewed', 'push_failed'].includes(row.status) &&
      !row.xeroInvoiceId &&
      !row.voidedAt &&
      !row.finalizedAt &&
      row.status !== 'locked');

  const canFinalizeRun = (row: BillingRun) =>
    row.canFinalize ??
    (['draft', 'previewed', 'push_failed', 'pushed'].includes(row.status) &&
      !row.voidedAt &&
      !row.finalizedAt &&
      !row.supersededByRunId &&
      (row.tripCount ?? 0) > 0 &&
      Number(row.totalAmount ?? row.total ?? 0) > 0);

  const canPushRun = (row: BillingRun) => canPushXero && Boolean(row.finalizedAt) && !row.xeroInvoiceId && !row.voidedAt;

  const handleDiscard = async (row: BillingRun) => {
    if (!canDiscardRun(row)) return;
    const ok = await confirm({
      title: 'Discard invoice run?',
      message: `Discard the ${clientName(row.clientId)} invoice run for ${row.period} (${row.tripCount ?? 0} trips, ${formatPeso(runTotal(row))}). The trips go back to the ready-to-bill pool. This cannot be undone.`,
      confirmLabel: 'Discard run',
      danger: true,
    });
    if (!ok) return;
    setDiscardingId(row.id);
    setActionError('');
    try {
      await billingApi.discardRun(row.id);
      toast(`Invoice run for ${clientName(row.clientId)}, ${row.period} discarded`);
      if (detailRunId === row.id) closeDetail();
      await reload();
    } catch (err) {
      setActionError(errorMessage(err, 'Failed to discard billing run'));
    } finally {
      setDiscardingId(null);
    }
  };

  const handleFinalize = async (data: InvoiceRunDetail) => {
    const row = data.run;
    if (!canFinalizeRun(row)) return;
    const ok = await confirm({
      title: 'Finalise invoice?',
      message: `Finalise invoice for ${clientName(row.clientId)}, ${row.period}: ${data.trips.length} trips, ${formatPeso(runTotal(row))} incl. VAT. Lines will be locked and the run can no longer be discarded.`,
      confirmLabel: 'Finalise invoice',
    });
    if (!ok) return;
    setFinalizingId(row.id);
    setDetailError('');
    try {
      const res = await billingApi.finalizeRun(row.id);
      toast(`Invoice ${res.run?.invoiceNumber ? `${res.run.invoiceNumber} ` : ''}finalised for ${clientName(row.clientId)}`);
      await Promise.all([reload(), loadDetail(row.id)]);
    } catch (err) {
      setDetailError(errorMessage(err, 'Failed to finalise invoice'));
    } finally {
      setFinalizingId(null);
    }
  };

  const handlePushXero = async (row: BillingRun) => {
    const ok = await confirm({
      title: 'Push to Xero?',
      message: `Send ${runName(row)} to Xero as a draft invoice (${row.tripCount ?? 0} trips, ${formatPeso(runTotal(row))} incl. VAT). Its trips will be marked billed.`,
      confirmLabel: 'Push to Xero',
    });
    if (!ok) return;
    setPushingId(row.id);
    setActionError('');
    setDetailError('');
    try {
      const res = await billingApi.pushToXero(row.id);
      if (res.xero?.success) {
        toast(`Pushed to Xero${res.xero.invoiceId ? ` (${res.xero.invoiceId})` : ''}`);
      } else {
        const msg = `Xero push failed: ${res.xero?.message ?? 'unknown error'}`;
        if (detailRunId === row.id) setDetailError(msg);
        else setActionError(msg);
      }
      await reload();
      if (detailRunId === row.id) await loadDetail(row.id);
    } catch (err) {
      const msg = errorMessage(err, 'Failed to push invoice to Xero');
      if (detailRunId === row.id) setDetailError(msg);
      else setActionError(msg);
    } finally {
      setPushingId(null);
    }
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) {
      setCreateError('Select a client');
      return;
    }
    setSaving(true);
    setCreateError('');
    try {
      const created = await billingApi.createRun({ clientId, period });
      setShowAdd(false);
      setPeriod(currentMonthPeriod());
      setClientId('');
      toast(`Invoice run created for ${clientName(created.clientId ?? clientId)} — review the lines, then finalise`);
      reload();
      if (created?.id) void loadDetail(created.id);
    } catch (err) {
      setCreateError(errorMessage(err, 'Failed to create billing run'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'invoiceNumber',
      header: 'Invoice',
      value: (row: BillingRun) => row.invoiceNumber ?? '',
      render: (row: BillingRun) => row.invoiceNumber ?? <span className="text-muted">Draft</span>,
    },
    {
      key: 'clientId',
      header: 'Client',
      value: (row: BillingRun) => clientNameById.get(row.clientId ?? '') ?? '',
      render: (row: BillingRun) => clientNameById.get(row.clientId ?? '') ?? '—',
    },
    { key: 'period', header: 'Period' },
    {
      key: 'status',
      header: 'Status',
      value: (row: BillingRun) => row.status,
      render: (row: BillingRun) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'tripCount',
      header: 'Trips',
      align: 'right' as const,
      value: (row: BillingRun) => row.tripCount ?? null,
      render: (row: BillingRun) => <span className="money-cell">{row.tripCount ?? '—'}</span>,
    },
    {
      key: 'totalAmount',
      header: 'Total',
      align: 'right' as const,
      value: (row: BillingRun) => (runTotal(row) ? Number(runTotal(row)) : null),
      render: (row: BillingRun) => <Money value={runTotal(row)} />,
    },
    {
      key: 'xeroInvoiceId',
      header: 'Xero',
      value: (row: BillingRun) => row.xeroInvoiceId ?? '',
      render: (row: BillingRun) => row.xeroInvoiceId ?? '—',
    },
    {
      key: 'createdAt',
      header: 'Created',
      value: (row: BillingRun) => row.createdAt,
      render: (row: BillingRun) => formatDate(row.createdAt),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: BillingRun) => (
        <div className="row" style={{ gap: '0.35rem' }}>
          {canFinalizeRun(row) ? (
            <Button type="button" size="sm" onClick={() => loadDetail(row.id)}>
              Review &amp; finalise
            </Button>
          ) : (
            <Button type="button" size="sm" variant="secondary" onClick={() => loadDetail(row.id)}>
              View
            </Button>
          )}
          {canPushRun(row) && (
            <Button
              type="button"
              size="sm"
              loading={pushingId === row.id}
              onClick={() => handlePushXero(row)}
            >
              Push to Xero
            </Button>
          )}
          {canDiscardRun(row) && (
            <Button
              type="button"
              size="sm"
              variant="danger"
              loading={discardingId === row.id}
              onClick={() => handleDiscard(row)}
            >
              Discard
            </Button>
          )}
        </div>
      ),
    },
  ];

  const markReadyBlocker = (row: Trip): string | null => {
    if (row.status !== 'completed') return `Trip is ${humanize(row.status).toLowerCase()}; only completed trips can be marked.`;
    const gates = gatesByTrip[row.id];
    if (!gates) return gatesLoading ? 'Checking billing checks…' : 'Billing checks could not be loaded.';
    if (!gates.allPass) {
      const failing = Object.entries(gates.gates)
        .filter(([, pass]) => !pass)
        .map(([key]) => GATE_LABELS[key] ?? humanize(key));
      return `Needs: ${failing.join(', ')}`;
    }
    return null;
  };

  const detailRun = detail?.run;

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Billing</h1>
            <p className="text-muted">Turn completed trips into client invoices</p>
          </div>
          <Button type="button" onClick={() => { setCreateError(''); setShowAdd(true); }}>New invoice run</Button>
        </div>

        <ol className="billing-steps" aria-label="Billing steps">
          <li>Mark trips ready to bill</li>
          <li>Create invoice run</li>
          <li>Review &amp; finalise</li>
          <li>Push to Xero</li>
          <li>Mark collected</li>
        </ol>

        <div className="page-header">
          <div>
            <h2>1. Ready-to-bill review</h2>
            <p className="text-muted">
              Completed trips waiting for billing checks. Mark them ready to bill so they are picked up by the next invoice run.
            </p>
          </div>
        </div>

        {markError && (
          <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{markError}</p>
        )}

        <DataTable
          searchPlaceholder="Filter review queue…"
          columns={[
            {
              key: 'tripNumber',
              header: 'Trip',
              value: (row: Trip) => row.tripNumber ?? row.reference ?? '',
              render: (row: Trip) => tripRef(row),
            },
            {
              key: 'clientId',
              header: 'Client',
              value: (row: Trip) => row.clientName ?? clientNameById.get(row.clientId ?? '') ?? '',
              render: (row: Trip) => row.clientName ?? clientNameById.get(row.clientId ?? '') ?? '—',
            },
            {
              key: 'status',
              header: 'Status',
              value: (row: Trip) => row.status,
              render: (row: Trip) => <StatusBadge status={row.status} size="sm" />,
            },
            {
              key: 'gates',
              header: 'Ready-to-bill gates',
              value: (row: Trip) => {
                const gates = gatesByTrip[row.id];
                return gates?.allPass ?? false;
              },
              render: (row: Trip) => {
                const gates = gatesByTrip[row.id];
                if (!gates) return gatesLoading ? <span className="text-muted">Checking…</span> : '—';
                const scansFail = gates.gates.scans_resolved === false;
                return (
                  <div className="gate-chip-row">
                    {Object.entries(gates.gates).map(([key, pass]) => (
                      <span key={key} className={`gate-chip ${pass ? 'gate-chip--pass' : 'gate-chip--fail'}`}>
                        {pass ? '✓' : '✗'} {GATE_LABELS[key] ?? humanize(key)}
                      </span>
                    ))}
                    {scansFail && (
                      <a
                        href={`/app/dispatch/trips/${row.id}`}
                        className="gate-chip gate-chip--fail"
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Reconcile scans →
                      </a>
                    )}
                  </div>
                );
              },
            },
            {
              key: 'actions',
              header: '',
              sortable: false,
              render: (row: Trip) => {
                const alreadyReady = row.status === 'ready_to_bill';
                if (alreadyReady) {
                  return <span className="text-ok" style={{ fontSize: '0.8125rem' }}>Ready to bill</span>;
                }
                const blocker = markReadyBlocker(row);
                return (
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={Boolean(blocker) || markingReady === row.id}
                      title={blocker ?? undefined}
                      loading={markingReady === row.id}
                      onClick={() => handleMarkReady(row)}
                    >
                      Mark ready to bill
                    </Button>
                    {blocker && <div className="text-muted helper-text">{blocker}</div>}
                  </div>
                );
              },
            },
          ]}
          data={reviewQueue ?? []}
          keyField="id"
          loading={queueLoading}
          dense
          emptyMessage="No trips awaiting review. Completed trips appear here automatically."
        />

        <div className="page-header" style={{ marginTop: '2rem' }}>
          <div>
            <h2>2–5. Invoice runs</h2>
            <p className="text-muted">
              Create a run per client and month, review its trips, finalise, push to Xero, then mark it collected once paid.
            </p>
          </div>
        </div>

        {actionError && (
          <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{actionError}</p>
        )}
        <DataTable
          columns={columns}
          data={runs ?? []}
          keyField="id"
          loading={loading}
          loadError={runsLoadError ? `Could not load billing runs: ${runsLoadError}` : null}
          onRetry={() => void reload()}
          dense
          searchPlaceholder="Filter invoice runs…"
          emptyMessage="No invoice runs yet. Mark trips ready to bill above, then create a run."
        />

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New invoice run">
          <form onSubmit={handleAdd}>
            {createError && <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{createError}</div>}
            <Select
              label="Client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
            >
              <option value="">Select client…</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.tradeName || c.legalName}</option>
              ))}
            </Select>
            <Input
              label="Billing month"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              required
            />
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-md)' }}>
              Includes this client&apos;s trips already marked ready to bill for that month. You can review the lines before finalising.
            </p>
            <Button type="submit" loading={saving}>Create run</Button>
          </form>
        </Modal>

        <Modal
          open={detailRunId !== null}
          onClose={closeDetail}
          title={
            detailRun
              ? `${detailRun.invoiceNumber ? `Invoice ${detailRun.invoiceNumber}` : 'Draft invoice'} · ${clientName(detailRun.clientId)}`
              : 'Invoice run'
          }
          size="lg"
        >
          {detailLoading && !detail && <p className="text-muted">Loading…</p>}
          {detailError && <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{detailError}</p>}
          {detail && detailRun && (
            <div>
              <div className="grid-3" style={{ marginBottom: 'var(--space-lg)' }}>
                <div className="stat-block">
                  <span className="stat-value"><StatusBadge status={detailRun.status} size="sm" /></span>
                  <span className="stat-label">Status · {detailRun.period}</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{formatPeso(runTotal(detailRun))}</span>
                  <span className="stat-label">
                    Total incl. VAT
                    {detailRun.subtotal ? ` (${formatPeso(detailRun.subtotal)} + ${formatPeso(detailRun.vatAmount)} VAT)` : ''}
                  </span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{detail.trips.length}</span>
                  <span className="stat-label">Trips</span>
                </div>
              </div>

              {detailRun.finalizedAt && (
                <p className="text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
                  Finalised {formatDateTime(detailRun.finalizedAt)}
                  {detailRun.externalReference ? ` · Ref ${detailRun.externalReference}` : ''}
                </p>
              )}
              {detailRun.xeroInvoiceId ? (
                <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
                  Xero reference: <strong>{detailRun.xeroInvoiceId}</strong>
                  {detailRun.xeroSyncState === 'stub' ? ' (Xero not connected — recorded locally)' : ''}
                </p>
              ) : detailRun.xeroSyncError ? (
                <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
                  Last Xero push failed: {detailRun.xeroSyncError}
                </p>
              ) : null}

              <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
                {canFinalizeRun(detailRun) && (
                  <Button
                    type="button"
                    size="sm"
                    loading={finalizingId === detailRun.id}
                    onClick={() => handleFinalize(detail)}
                  >
                    Finalise invoice
                  </Button>
                )}
                {canPushRun(detailRun) && (
                  <Button
                    type="button"
                    size="sm"
                    loading={pushingId === detailRun.id}
                    onClick={() => handlePushXero(detailRun)}
                  >
                    Push to Xero
                  </Button>
                )}
                {detailRun.finalizedAt && detailRun.invoiceNumber && !detailRun.voidedAt && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    loading={downloadingInvoiceId === detailRun.id}
                    onClick={() => void handleDownloadInvoice(detailRun)}
                  >
                    Download invoice PDF
                  </Button>
                )}
                {detailRun.finalizedAt && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      loading={markingCollectedId === detailRun.id}
                      disabled={detailRun.status === 'paid' || detailRun.paymentStatus === 'paid'}
                      onClick={() => handleMarkCollected(detailRun)}
                    >
                      {detailRun.status === 'paid' || detailRun.paymentStatus === 'paid' ? 'Collected' : 'Mark collected'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => { setCreditError(''); setShowCreditNote(true); }}
                    >
                      Issue credit note
                    </Button>
                  </>
                )}
                {canDiscardRun(detailRun) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    loading={discardingId === detailRun.id}
                    onClick={() => handleDiscard(detailRun)}
                  >
                    Discard run
                  </Button>
                )}
              </div>

              {detail.frozenLines?.length ? (
                <>
                  <h3 style={{ marginBottom: 'var(--space-sm)' }}>Invoice lines (locked)</h3>
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    {detail.frozenLines.map((line) => (
                      <div key={line.tripId} className="frozen-line-row">
                        <span>{line.tripNumber ?? 'Trip'}</span>
                        <span className="money-cell">{formatPeso(line.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ marginBottom: 'var(--space-sm)' }}>Trips on this invoice</h3>
                  <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-sm)' }}>
                    Not finalised yet. Check these trips and amounts before finalising; finalising locks them.
                  </p>
                  {detail.trips.length ? (
                    <div style={{ marginBottom: 'var(--space-lg)' }}>
                      {detail.trips.map((trip) => (
                        <div key={trip.id} className="frozen-line-row">
                          <span>
                            {tripRef(trip)}
                            {trip.reference && trip.tripNumber ? <span className="text-muted"> · {trip.reference}</span> : null}
                          </span>
                          <span className="money-cell">{formatPeso(tripAmount(trip))}</span>
                        </div>
                      ))}
                      <div className="frozen-line-row" style={{ fontWeight: 600 }}>
                        <span>Total incl. VAT</span>
                        <span className="money-cell">{formatPeso(runTotal(detailRun))}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted" style={{ marginBottom: 'var(--space-lg)' }}>
                      No trips in this run. Mark trips ready to bill for this client and month, or discard this run.
                    </p>
                  )}
                </>
              )}

              <h3 style={{ marginBottom: 'var(--space-sm)' }}>Credit notes</h3>
              {detail.creditNotes.length ? (
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  {detail.creditNotes.map((note) => (
                    <div key={note.id} className="frozen-line-row">
                      <span>{note.creditNoteNumber} · {note.reason}</span>
                      <span className="money-cell">{formatPeso(note.total)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted" style={{ marginBottom: 'var(--space-lg)' }}>No credit notes issued.</p>
              )}
            </div>
          )}
        </Modal>

        <Modal open={showCreditNote} onClose={() => setShowCreditNote(false)} title="Issue credit note" size="sm">
          <form onSubmit={handleCreateCreditNote}>
            {creditError && <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>{creditError}</div>}
            {detailRun && (
              <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: 'var(--space-md)' }}>
                Invoice {detailRun.invoiceNumber ?? ''} total: {formatPeso(runTotal(detailRun))} incl. VAT.
                Enter the credit amount before VAT; VAT is added on top.
              </p>
            )}
            <Input
              label="Amount (₱, excluding VAT)"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              required
            />
            <div className="input-group">
              <label className="input-label" htmlFor="credit-reason">Reason</label>
              <textarea
                id="credit-reason"
                className="input-field"
                rows={3}
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                required
              />
            </div>
            <Button type="submit" loading={creditSaving}>Issue credit note</Button>
          </form>
        </Modal>
      </div>
      <style>{`
        .billing-steps {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs) var(--space-md);
          list-style: none;
          counter-reset: step;
          padding: var(--space-sm) var(--space-md);
          margin: 0 0 var(--space-lg);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          font-size: 0.8125rem;
        }
        .billing-steps li {
          counter-increment: step;
        }
        .billing-steps li::before {
          content: counter(step) ". ";
          font-weight: 600;
        }
        .billing-steps li:not(:last-child)::after {
          content: " →";
          color: var(--color-text-muted, #888);
        }
        .money-cell {
          display: block;
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .helper-text {
          font-size: 0.75rem;
          margin-top: 2px;
          max-width: 16rem;
        }
        .gate-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }
        .gate-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .gate-chip--pass {
          background: rgba(46, 139, 87, 0.12);
          color: #2E8B57;
        }
        .gate-chip--fail {
          background: rgba(200, 60, 50, 0.12);
          color: #C83C32;
        }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
        }
        .frozen-line-row {
          display: flex;
          justify-content: space-between;
          gap: var(--space-md);
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.875rem;
        }
      `}</style>
    </Layout>
  );
}
